import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { settingsApi } from '../../api/settings';
import {
  CANT_ESTIMATE_RATES_LINE,
  CANT_ESTIMATE_SIZE_LINE,
  estimatePlotTrackGenerateCostUsd,
  formatGenerateEstimateLine,
  pricingFromCatalog,
  resolveStoryBoardModelId,
} from '../../utils/estimatePlotTrackGenerateCost';
import { ESTIMATE_SLOW_MS } from '../../utils/estimateSceneWritingCost';
import {
  SUGGEST_NOT_YET,
  SUGGEST_SCENES,
  SUGGEST_SCENES_FOR,
  SUGGEST_SCENES_HINT,
  SUGGEST_SCENES_NOTE_LABEL,
} from '../../utils/writerCopy';
import './PlotTrackGenerateConfirm.css';

function catalogModels(catalogRes) {
  return catalogRes?.data?.models || catalogRes?.models || [];
}

function pricingForModel(catalog, modelId) {
  const direct = pricingFromCatalog(catalog, modelId);
  const prompt = Number(direct?.prompt);
  const completion = Number(direct?.completion);
  if (Number.isFinite(prompt) && Number.isFinite(completion)) return direct;
  if (!modelId || !Array.isArray(catalog)) return null;
  const folded = String(modelId).toLowerCase();
  const entry = catalog.find((model) => String(model?.id || '').toLowerCase() === folded);
  return entry?.pricing || null;
}

/**
 * One confirm for Suggest scenes. Optional note, one cost line.
 * The button waits only while rates are still loading. A missing rate
 * or prompt size uses the same "you can still confirm" lines as the
 * other Generate confirms. This is not a spend cap.
 */
export default function PlanPartConfirm({
  partName,
  fillsSpine = false,
  estimatedInputTokens = null,
  promptChars = null,
  sizePending = false,
  onConfirm,
  onCancel,
}) {
  const [note, setNote] = useState('');
  const [modelId, setModelId] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [ratesState, setRatesState] = useState('loading');
  const [slow, setSlow] = useState(false);
  const title = SUGGEST_SCENES_FOR(partName || 'this sequence');

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), ESTIMATE_SLOW_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRatesState('loading');
    const pull = async () => {
      const [settingsRes, catalogRes] = await Promise.all([
        settingsApi.getSettings(),
        settingsApi.getOpenRouterCatalog().catch(() => null),
      ]);
      const settings = settingsRes?.data || settingsRes || {};
      const id = resolveStoryBoardModelId(settings?.model_preferences);
      return { id, models: catalogModels(catalogRes) };
    };
    (async () => {
      let lastId = '';
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const result = await pull();
          if (cancelled) return;
          lastId = result.id;
          if (result.models.length) {
            setModelId(result.id);
            setCatalog(result.models);
            setRatesState('ready');
            return;
          }
        } catch {
          if (cancelled) return;
        }
      }
      if (!cancelled) {
        setModelId(lastId);
        setCatalog([]);
        setRatesState('missing');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pricing = pricingForModel(catalog, modelId);
  const hasSize = estimatedInputTokens != null || promptChars != null;
  const band = ratesState === 'ready' && hasSize
    ? estimatePlotTrackGenerateCostUsd(pricing, {
      fillsSpine,
      estimatedInputTokens,
      promptChars,
    })
    : null;
  const waitingOnRates = ratesState === 'loading';
  const waitingOnSize = ratesState === 'ready' && !hasSize && sizePending;
  let estimateLine = 'Loading estimate…';
  if (band) {
    estimateLine = formatGenerateEstimateLine(band) || CANT_ESTIMATE_RATES_LINE;
  } else if (waitingOnRates || waitingOnSize) {
    estimateLine = slow
      ? (waitingOnRates ? CANT_ESTIMATE_RATES_LINE : CANT_ESTIMATE_SIZE_LINE)
      : 'Loading estimate…';
  } else if (ratesState === 'missing' || !pricing) {
    estimateLine = CANT_ESTIMATE_RATES_LINE;
  } else if (!hasSize) {
    estimateLine = CANT_ESTIMATE_SIZE_LINE;
  } else {
    estimateLine = CANT_ESTIMATE_RATES_LINE;
  }

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) onCancel?.();
  };

  return createPortal(
    <div className="pt-generate-confirm-backdrop" onClick={handleBackdrop}>
      <div
        className="pt-generate-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pt-plan-part-title"
      >
        <h3 id="pt-plan-part-title" className="pt-generate-confirm__title">
          {title}
        </h3>
        <p className="pt-generate-confirm__hint">{SUGGEST_SCENES_HINT(partName || 'this sequence')}</p>
        <label className="pt-generate-confirm__note">
          {SUGGEST_SCENES_NOTE_LABEL}
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
          />
        </label>
        <p className="pt-generate-confirm__estimate" data-testid="pt-plan-part-estimate">
          {estimateLine}
        </p>
        <div className="pt-generate-confirm__actions">
          <button
            type="button"
            className="pt-generate-confirm__btn pt-generate-confirm__btn--cancel"
            onClick={onCancel}
          >
            {SUGGEST_NOT_YET}
          </button>
          <button
            type="button"
            className="pt-generate-confirm__btn pt-generate-confirm__btn--confirm"
            onClick={() => onConfirm?.(note.trim())}
            disabled={waitingOnRates && !slow}
          >
            {SUGGEST_SCENES}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
